import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Paper, Popper, Tooltip, Typography } from '@mui/material'
import type { Instance as PopperInstance } from '@popperjs/core'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddIcon from '@mui/icons-material/Add'
import { Icon } from '@iconify/react'
import styled from 'styled-components'
import {
  getMilestoneStatus,
  MilestoneDetail,
} from '../../../../../../../../domain/entity/MilestoneEntity'
import { CalendarDateVO } from '../../../../../../../../domain/value-object/CalendarDateVO'
import { colorPalette } from '../../../../../../../style/colorPalette'
import BigFireIcon from '../../../../../../../components/icons/BigFireIcon'
import { intl } from '../../../../../../../../i18n'

export type MilestoneWithPosition = {
  milestone: MilestoneDetail
  position?: number
}

const IconContainer = styled.span`
  display: flex;
  align-items: center;
  position: relative;
  line-height: 1;
`

const PopoverContent = styled.div`
  padding: 8px 12px;
`

const PopoverItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const PopoverItemIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 1;
`

const PopoverItemWrapper = styled.div<{ $marginTop?: number }>`
  margin-top: ${({ $marginTop }) => $marginTop ?? 0}px;
`

const ClickableSpan = styled.span`
  cursor: pointer;
`

type PositionedMilestoneIconProps = {
  $position: number
}

const PositionedMilestoneIcon = styled.span<PositionedMilestoneIconProps>`
  position: absolute;
  left: ${({ $position }) => $position}%;
  transform: translateX(-50%);
  cursor: pointer;
`

const StyledCheckCircleIcon = styled(CheckCircleIcon)`
  font-size: 14px;
  flex-shrink: 0;
  color: ${colorPalette.green[5]};
`

const AchievedBadge = styled(CheckCircleIcon)`
  position: absolute;
  top: -3px;
  right: -2px;
  font-size: 14px;
  color: ${colorPalette.green[5]};
  background-color: ${colorPalette.monotone[0]};
  border-radius: 50%;
`

const StyledCheckCircleOutlineIcon = styled(CheckCircleOutlineIcon)`
  font-size: 14px;
  flex-shrink: 0;
  color: ${colorPalette.monotone[4]};
`

const DateTypography = styled(Typography)`
  font-size: 11px;
  color: ${colorPalette.monotone[5]};
  white-space: nowrap;
`

const NameTypography = styled(Typography)`
  font-size: 12px;
  flex: 1;
  min-width: 0;
`

const ClickableName = styled(Typography)`
  font-size: 12px;
  flex: 1;
  min-width: 0;
  color: ${colorPalette.skyBlue[7]};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const EmptyCellContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const ButtonWrapper = styled(Box)`
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AddButton = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${colorPalette.skyBlue[1]};
  color: ${colorPalette.skyBlue[7]};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${colorPalette.skyBlue[2]};
  }
`

const SmallAddIcon = styled(AddIcon)`
  font-size: 15px;
`

const PopoverAddButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-right: 8px;
`

const PopoverBody = styled.div`
  display: flex;
  align-items: center;
`

const milestonePopoverPaperProps = {
  elevation: 1,
  sx: { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)' },
}

const formatMilestoneDate = (dateString: string): string =>
  CalendarDateVO.of(new Date(dateString)).format('M/D (ddd)')

const StatusIcon = ({ milestone }: { milestone: MilestoneDetail }) => {
  const status = getMilestoneStatus(milestone)

  if (status === 'achieved') {
    return <StyledCheckCircleIcon />
  }
  if (status === 'overdue') {
    return <BigFireIcon style={{ fontSize: 14, flexShrink: 0 }} />
  }
  return <StyledCheckCircleOutlineIcon />
}

const MilestoneIconWithBadge = ({
  milestone,
}: {
  milestone: MilestoneDetail
}) => (
  <IconContainer>
    <Icon
      icon={milestone.iconName ?? 'mdi:cards-diamond'}
      width={20}
      height={20}
      color={milestone.color}
    />
    {milestone.achieved ? <AchievedBadge /> : null}
  </IconContainer>
)

type MilestonePopoverContentProps = {
  milestones: MilestoneWithPosition[]
  showDate?: boolean
  onEdit?: (uuid: string) => void
}

const MilestonePopoverItem = ({
  item,
  showDate,
  onEdit,
}: {
  item: MilestoneWithPosition
  showDate?: boolean
  onEdit?: (uuid: string) => void
}) => {
  const { milestone } = item
  const handleNameClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(milestone.uuid)
  }

  return (
    <PopoverItem>
      {showDate && (
        <DateTypography variant="body2">
          {formatMilestoneDate(milestone.milestoneDate)}
        </DateTypography>
      )}
      <PopoverItemIcon>
        <Icon
          icon={milestone.iconName ?? 'mdi:cards-diamond'}
          width={16}
          height={16}
          color={milestone.color}
        />
      </PopoverItemIcon>
      {onEdit ? (
        <ClickableName variant="body2" onClick={handleNameClick}>
          {milestone.name}
        </ClickableName>
      ) : (
        <NameTypography variant="body2">{milestone.name}</NameTypography>
      )}
      <StatusIcon milestone={milestone} />
    </PopoverItem>
  )
}

const MilestonePopoverContent = ({
  milestones,
  showDate = false,
  onEdit,
}: MilestonePopoverContentProps) => (
  <PopoverContent>
    {milestones.map((item, index) => (
      <PopoverItemWrapper
        key={item.milestone.uuid}
        $marginTop={index > 0 ? 4 : 0}
      >
        <MilestonePopoverItem item={item} showDate={showDate} onEdit={onEdit} />
      </PopoverItemWrapper>
    ))}
  </PopoverContent>
)

const PopoverAddButton = ({ onAdd }: { onAdd: () => void }) => (
  <PopoverAddButtonWrapper>
    <Tooltip
      arrow={true}
      title={intl.formatMessage({ id: 'milestone.popover.add' })}
    >
      <AddButton
        onClick={e => {
          e.stopPropagation()
          onAdd()
        }}
      >
        <SmallAddIcon />
      </AddButton>
    </Tooltip>
  </PopoverAddButtonWrapper>
)

type MilestoneIconsProps = {
  milestones: MilestoneWithPosition[]
  positioned?: boolean
  showMilestoneName?: boolean
  milestoneNameResetSeq?: number
  popperZIndex?: number
  onEdit?: (uuid: string) => void
  onAdd?: () => void
}

export const MilestoneIcons = ({
  milestones,
  positioned = false,
  showMilestoneName = true,
  milestoneNameResetSeq = 0,
  popperZIndex = 0,
  onEdit,
  onAdd,
}: MilestoneIconsProps) => {
  if (positioned) {
    return (
      <PositionedMilestoneIcons
        milestones={milestones}
        showMilestoneName={showMilestoneName}
        milestoneNameResetSeq={milestoneNameResetSeq}
        popperZIndex={popperZIndex}
        onEdit={onEdit}
      />
    )
  }
  return (
    <SingleMilestoneIcon
      milestones={milestones}
      showMilestoneName={showMilestoneName}
      milestoneNameResetSeq={milestoneNameResetSeq}
      popperZIndex={popperZIndex}
      onEdit={onEdit}
      onAdd={onAdd}
    />
  )
}

const PINNED_OVER_POPPER_STYLE_ID = 'milestone-popper-pinned-zindex'

const ensurePinnedOverPopperStyle = () => {
  if (typeof document === 'undefined') return
  const desired = `
    .ag-pinned-left-header,
    .ag-pinned-right-header {
      position: relative;
      z-index: 950;
    }
  `
  const existing = document.getElementById(PINNED_OVER_POPPER_STYLE_ID)
  if (existing) {
    if (existing.textContent !== desired) existing.textContent = desired
    return
  }
  const style = document.createElement('style')
  style.id = PINNED_OVER_POPPER_STYLE_ID
  style.textContent = desired
  document.head.appendChild(style)
}

const useHeaderTransformTracking = (
  popperRef: React.MutableRefObject<PopperInstance | null>,
  anchor: HTMLElement | null
): boolean => {
  const [anchorVisible, setAnchorVisible] = useState(true)

  useEffect(() => {
    ensurePinnedOverPopperStyle()
  }, [])

  useEffect(() => {
    if (!anchor) return
    const viewport =
      anchor.closest('.ag-header-viewport') ??
      anchor.closest('.ag-center-cols-viewport')
    const target =
      anchor.closest('.ag-header-container') ??
      anchor.closest('.ag-center-cols-container')
    const scrollSource = anchor.ownerDocument.querySelector(
      '.ag-center-cols-viewport'
    )
    const gridRoot = anchor.closest('.ag-root-wrapper')
    if (!target || !viewport) return

    const update = () => {
      const a = anchor.getBoundingClientRect()
      const v = viewport.getBoundingClientRect()
      setAnchorVisible(a.right > v.left && a.left < v.right)
      popperRef.current?.update()
    }
    update()

    const observer = new MutationObserver(update)
    observer.observe(target, {
      attributes: true,
      attributeFilter: ['style'],
    })
    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(viewport)
    if (gridRoot) resizeObserver.observe(gridRoot)
    scrollSource?.addEventListener('scroll', update, { passive: true })
    return () => {
      observer.disconnect()
      resizeObserver.disconnect()
      scrollSource?.removeEventListener('scroll', update)
    }
  }, [anchor, popperRef])

  return anchorVisible
}

const useMilestonePopperModifiers = (anchor: HTMLElement | null) => {
  return useMemo(() => {
    const boundary =
      anchor?.closest('.ag-header-viewport') ??
      anchor?.closest('.ag-center-cols-viewport') ??
      undefined
    return [
      { name: 'offset', options: { offset: [0, 4] } },
      { name: 'flip', enabled: false },
      ...(boundary
        ? [
            { name: 'preventOverflow', options: { boundary } },
            { name: 'hide', enabled: true },
            {
              name: 'clipAtBoundary',
              enabled: true,
              phase: 'afterWrite' as const,
              fn: ({
                state,
              }: {
                state: { elements: { popper: HTMLElement } }
              }) => {
                const boundaryRect = (
                  boundary as HTMLElement
                ).getBoundingClientRect()
                const popperRect = state.elements.popper.getBoundingClientRect()
                if (popperRect.right > boundaryRect.right) {
                  const clipRight = popperRect.right - boundaryRect.right
                  state.elements.popper.style.clipPath = `inset(-8px ${clipRight}px -8px -8px)`
                  const visibleWidth = popperRect.width - clipRight
                  if (visibleWidth < 20) {
                    state.elements.popper.style.pointerEvents = 'none'
                  }
                } else {
                  state.elements.popper.style.clipPath = ''
                  state.elements.popper.style.pointerEvents = ''
                }
              },
            },
          ]
        : []),
    ]
  }, [anchor])
}

let popperFocusCounter = 0

const bringPopperToFront = (e: React.MouseEvent) => {
  const popper = (e.currentTarget as HTMLElement).closest<HTMLElement>(
    '.MuiPopper-root'
  )
  if (!popper) return
  popper.style.zIndex = String(Math.min(++popperFocusCounter + 600, 900))
  if (!popper.dataset.hasResetListener) {
    popper.dataset.hasResetListener = '1'
    popper.addEventListener('mouseleave', () => {
      popper.style.removeProperty('z-index')
    })
  }
}

const getMilestoneNamePopperSx = (position = 0) => ({
  zIndex: 200 + Math.min(Math.round(position), 300),
  '&[data-popper-escaped="true"]': {
    visibility: 'hidden' as const,
    pointerEvents: 'none' as const,
  },
})

type SingleMilestoneIconProps = {
  milestones: MilestoneWithPosition[]
  showMilestoneName: boolean
  milestoneNameResetSeq: number
  popperZIndex: number
  onEdit?: (uuid: string) => void
  onAdd?: () => void
}

const SingleMilestoneIcon = ({
  milestones,
  showMilestoneName,
  milestoneNameResetSeq,
  popperZIndex,
  onEdit,
  onAdd,
}: SingleMilestoneIconProps) => {
  const iconRef = useRef<HTMLSpanElement | null>(null)
  const popperRef = useRef<PopperInstance | null>(null)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [toggled, setToggled] = useState(false)

  useEffect(() => {
    setAnchor(iconRef.current)
  }, [])

  useEffect(() => {
    setToggled(false)
  }, [showMilestoneName, milestoneNameResetSeq])

  const anchorVisible = useHeaderTransformTracking(popperRef, anchor)
  const modifiers = useMilestonePopperModifiers(anchor)
  const popperSx = useMemo(
    () => getMilestoneNamePopperSx(popperZIndex),
    [popperZIndex]
  )

  const open = showMilestoneName !== toggled && anchorVisible
  const firstMilestone = milestones[0].milestone

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setToggled(prev => !prev)
  }, [])

  const handleEdit = useCallback(
    (uuid: string) => {
      onEdit?.(uuid)
    },
    [onEdit]
  )

  return (
    <>
      <ClickableSpan ref={iconRef} onClick={handleClick}>
        <MilestoneIconWithBadge milestone={firstMilestone} />
      </ClickableSpan>
      {anchor && (
        <Popper
          open={open}
          anchorEl={anchor}
          placement="right"
          modifiers={modifiers}
          popperRef={popperRef}
          sx={popperSx}
        >
          <Paper
            {...milestonePopoverPaperProps}
            onMouseDown={bringPopperToFront}
          >
            <PopoverBody>
              <MilestonePopoverContent
                milestones={milestones}
                showDate={false}
                onEdit={onEdit ? handleEdit : undefined}
              />
              {onAdd && <PopoverAddButton onAdd={onAdd} />}
            </PopoverBody>
          </Paper>
        </Popper>
      )}
    </>
  )
}

type PositionedMilestoneIconsProps = {
  milestones: MilestoneWithPosition[]
  showMilestoneName: boolean
  milestoneNameResetSeq: number
  popperZIndex: number
  onEdit?: (uuid: string) => void
}

const PositionedContainer = styled.span`
  display: contents;
`

const PositionedMilestoneIcons = ({
  milestones,
  showMilestoneName,
  milestoneNameResetSeq,
  popperZIndex,
  onEdit,
}: PositionedMilestoneIconsProps) => {
  const containerRef = useRef<HTMLSpanElement | null>(null)
  const popperRef = useRef<PopperInstance | null>(null)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [toggled, setToggled] = useState(false)

  const groupedByPosition = useMemo(() => {
    const groups = new Map<number, MilestoneWithPosition[]>()
    for (const item of milestones) {
      const pos = item.position ?? 50
      if (!groups.has(pos)) {
        groups.set(pos, [])
      }
      groups.get(pos)!.push(item)
    }
    return Array.from(groups.entries()).map(([pos, items]) => ({
      position: pos,
      items,
    }))
  }, [milestones])

  useEffect(() => {
    setToggled(false)
  }, [showMilestoneName, milestoneNameResetSeq])

  useEffect(() => {
    const firstIcon = containerRef.current?.querySelector<HTMLElement>(
      '[data-milestone-position]'
    )
    if (firstIcon) setAnchor(firstIcon)
  }, [groupedByPosition])

  const anchorVisible = useHeaderTransformTracking(popperRef, anchor)
  const modifiers = useMilestonePopperModifiers(anchor)
  const popperSx = useMemo(
    () => getMilestoneNamePopperSx(popperZIndex),
    [popperZIndex]
  )

  const open = showMilestoneName !== toggled && anchorVisible

  const handleIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAnchor(e.currentTarget)
    setToggled(prev => !prev)
  }

  const handleEdit = (uuid: string) => {
    onEdit?.(uuid)
  }

  return (
    <PositionedContainer ref={containerRef}>
      {groupedByPosition.map(({ position, items }) => (
        <PositionedMilestoneIcon
          key={`pos-${position}`}
          $position={position}
          data-milestone-position={position}
          onClick={handleIconClick}
        >
          <MilestoneIconWithBadge milestone={items[0].milestone} />
        </PositionedMilestoneIcon>
      ))}
      {anchor && (
        <Popper
          open={open}
          anchorEl={anchor}
          placement="right"
          modifiers={modifiers}
          popperRef={popperRef}
          sx={popperSx}
        >
          <Paper
            {...milestonePopoverPaperProps}
            onMouseDown={bringPopperToFront}
          >
            <MilestonePopoverContent
              milestones={milestones}
              showDate={true}
              onEdit={onEdit ? handleEdit : undefined}
            />
          </Paper>
        </Popper>
      )}
    </PositionedContainer>
  )
}

export const EmptyMilestoneCell = ({ onAdd }: { onAdd: () => void }) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <EmptyCellContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered && (
        <ButtonWrapper>
          <Tooltip
            arrow={true}
            title={intl.formatMessage({ id: 'milestone.popover.add' })}
          >
            <AddButton
              onClick={e => {
                e.stopPropagation()
                onAdd()
              }}
            >
              <SmallAddIcon />
            </AddButton>
          </Tooltip>
        </ButtonWrapper>
      )}
    </EmptyCellContainer>
  )
}

const PositionedAddButtonContainer = styled(Box)`
  position: absolute;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: auto;
`

const HoverableCell = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;

  &:hover ${PositionedAddButtonContainer} {
    opacity: 1;
  }
`

export const AddButtonForPositionedCell = ({
  onAdd,
}: {
  onAdd: () => void
}) => (
  <HoverableCell>
    <PositionedAddButtonContainer>
      <Tooltip
        arrow={true}
        title={intl.formatMessage({ id: 'milestone.popover.add' })}
      >
        <AddButton
          onClick={e => {
            e.stopPropagation()
            onAdd()
          }}
        >
          <SmallAddIcon />
        </AddButton>
      </Tooltip>
    </PositionedAddButtonContainer>
  </HoverableCell>
)
